import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, Lock } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const principles = [
    { 
      icon: Shield, 
      title: t('about.value1'),
      description: t('about.value1Desc')
    },
    { 
      icon: Target, 
      title: t('about.value2'),
      description: t('about.value2Desc')
    },
    { 
      icon: Lock, 
      title: t('about.value3'),
      description: t('about.value3Desc')
    },
  ];

  return (
    <section 
      id="about"
      data-testid="about-section"
      className="py-16 md:py-20 bg-background"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image - Abstract architectural visual */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWwlMjBnZW9tZXRyaWMlMjBsaW5lcyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4NzkxNDM3Mnww&ixlib=rb-4.1.0&q=85"
                alt="Abstract architectural structure"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A4A]/30 to-transparent" />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#60B5AF]/30 rounded-lg -z-10" />
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">
              {t('about.subtitle')}
            </p>
            <h2 
              data-testid="about-title"
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight mb-8"
            >
              {t('about.title')}
            </h2>

            {/* Mission */}
            <div className="mb-10">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                {t('about.mission')}
              </h3>
              <p className="font-body text-muted-foreground leading-relaxed text-lg">
                {t('about.missionText')}
              </p>
            </div>

            {/* Operating Principles - Enhanced */}
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-6">
                {t('about.values')}
              </h3>
              <div className="space-y-3">
                {principles.map((principle, index) => (
                  <div 
                    key={index}
                    data-testid={`about-value-${index}`}
                    className="group p-5 bg-[#F8F9FA] rounded-xl border-l-[3px] border-l-transparent hover:border-l-[#60B5AF] hover:bg-[#F8F9FA]/80 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-[#60B5AF]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#60B5AF]/20 transition-colors">
                        <principle.icon className="h-5 w-5 text-[#60B5AF]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-body font-semibold text-[#1A3A4A] mb-1">
                          {principle.title}
                        </h4>
                        <p className="font-body text-sm text-[#1A3A4A]/50 leading-relaxed">
                          {principle.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Closing italic line */}
              <p className="font-body text-sm text-[#1A3A4A]/40 italic mt-6 pl-1">
                {t('about.principlesClosing')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
